<script>
  import t from '#lib/i18n.svelte.js';
  import { page } from '$app/state';
  import { is_external_url } from '#lib/util.js';
  import Image from '#lib/Image.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';

  /**
   * @typedef {Object} NavModel
   * @property {NavItem[]} nav_items
   * @property {function(number):void} delete_item - Delete a nav item by index.
   * @property {function(Object):void} add_nav_item - Add a new nav item.
   */

  /**
   * @typedef {Object} NavItem
   * @property {string} href
   * @property {string} label
   * @property {string} class
   * @property {Object} [image]
   * @property {string} type - The type of nav item (e.g., 'weblink', 'spacer').
   * @property {boolean} [show_label_with_image] - Whether to show the label when an image is present.
   * @property {string} [size] - The size of the image (e.g., 'tall', 'normal', 'short').
   */

  let {
    /** @type {string} */
    id,
    class: css_class,
    /** @type {Array<NavItem>} */
    nav_items,
    /** @type {string} */
    current,
    /** @type {'row'|'column'} */
    orientation,
    /** @type {'start'|'center'|'end'} */
    align = 'start',
    /** @type {string} */
    text_style = 'text-control',
    /** @type {'text-compact'|'text-large'} */
    text_size = 'text-compact',
    /** @type {boolean} wether to only show the nav items before the first spacer */
    truncate_after_separator = false,
    /** @type {boolean} if true, the navigation will be displayed in editing mode */
    is_editing = false
  } = $props();


  /**
   * Groups navigation items into arrays separated by spacers and adds desktop-only class.
   * @param {Array<NavItem>} nav_items - The navigation items to group.
   * @param {boolean} truncate_after_separator - Whether to truncate after the first separator.
   * @returns {Array<Array<NavItem>>} An array of grouped navigation items.
   */
   function prepare_nav_items(nav_items, truncate_after_separator) {
      const result = [];
      let current_group = [];
      let add_desktop_only = false;
      let has_mobile_menu_breakpoint = false;

      for (const item of nav_items) {
        if (item.type === 'spacer') {
          if (current_group.length > 0) {
            result.push(current_group);
            current_group = [];
          }
        } else {
          if (add_desktop_only) {
            item.class = (item.class || '') + ' desktop-only';
          }
          current_group.push(item);
        }

        if (item.type === 'mobile_menu_breakpoint') {
          add_desktop_only = true;
          has_mobile_menu_breakpoint = true;
        }

        if (truncate_after_separator && item.type === 'separator') {
          break;
        }
      }

      if (current_group.length > 0) {
        result.push(current_group);
      }

      return {
        processed_nav_items: result,
        has_mobile_menu_breakpoint
      };
    }

    let { processed_nav_items, has_mobile_menu_breakpoint } = $derived(prepare_nav_items(nav_items, truncate_after_separator));

</script>


<nav 
  {id}
  class='navigation relative flex-{orientation} align-mobile-{align} {css_class} {text_style === 'text-heading1' ? 'py p-4 gap-y g-4' : ''}'
  data-current={current}
  class:has-mobile-menu-breakpoint={has_mobile_menu_breakpoint}
  class:blocking-overlay={is_editing}
>
  {#each processed_nav_items as group}
    <!-- Only render nav group if it contains items after filtering out user profile links for logged in users -->
    {#if group.filter(item => !(item.type === 'user_profile' && page.data.current_user)).length > 0}
      <div class="nav-group flex-{orientation} flex-wrap {orientation === 'column' ? 'full-width' : ''}">
        {#each group as { href, label, image, type, show_label_with_image, size, surface, class: css_class}}
          <!-- Existing item rendering logic -->
          {#if type === 'separator'}
            <div class='nav-item nav-separator {css_class}'></div>
          <!-- Show sign in button only for logged out users -->
          {:else if type === 'user_profile' && !page.data.current_user}
            <a
              class='nav-item nav-link {css_class} size-{size}'
              class:has-encapuslated-surface={ surface && !['ghostly','underlined'].includes(surface)}
              href='/login'
              style:--nav-item-size='{size}px'
            >
              <ButtonLabel 
                {text_style}
                align={align === 'center' ? 'center' : `${align}-stretch`} 
                variant='brand {surface ?? 'ghostly'} {text_size}'
              >{t('sign_in')}
              </ButtonLabel>
            </a>
          {:else if ['messages', 'calendar', 'posters', 'phone', 'email', 'weblink', 'entry', 'anchor_link'].includes(type)}
            {@const image_only = image && !show_label_with_image}
            <a
              class='nav-item nav-link {css_class} size-{size} {image_only ? "image-only" : ""}'
              class:has-encapuslated-surface={ surface && !['ghostly','underlined'].includes(surface)}
              href={is_editing ? '#' : href}
              target={is_external_url(href) ? '_blank' : undefined}
              rel={is_external_url(href) ? 'noopener noreferrer' : undefined}
              style:--nav-item-size='{size}px'
              {...(is_editing ? { disabled: true } : {})}
            >
              <!-- Problem: if <start_icon> was nested inside <ButtonLabel> with {#if image} condition, it would create a children snippet regardeless of the condition, causing issues with spacing in ButtonLabel, as the first-child is then an empty span for the icon -->
              <!-- Pattern derived from here: https://github.com/sveltejs/svelte/issues/12675#issuecomment-2757154203 -->
              {#snippet start_icon()}
                {@const scaled_size = (text_size === 'text-large' && orientation === 'column') ? size * 1.25 : size}
                <Image 
                  class='navigation-image'
                  alt={label} 
                  {...image} 
                  display_height={scaled_size}
                  display_width={image.width / image.height * scaled_size}
                  style='--dynamic-island-max-nav-item-size: {scaled_size}px;'
                />
              {/snippet}
              <!-- Show label when there's no image, otherwise only when explicitly included -->
              <ButtonLabel 
                {text_style}
                align={align === 'center' ? 'center' : `${align}-stretch`} 
                variant='brand {surface ?? 'ghostly'} {text_size}'
                start_icon={image ? start_icon : undefined}
                text={(!image || (image && show_label_with_image)) ? label : undefined}
              />
            </a>
          {/if}
        {/each}
      </div>
    {/if}
  {/each}
</nav>

<style lang='postcss'>

  .navigation {
    position: relative;
    .nav-group {
      max-width: 100%;
      &:not(:first-child) {
        justify-content: center;
      }
      &:not(:first-child):not(:last-child) {
        /* flex-basis: 0; */
        flex-grow: 0;
      }
      &:not(:first-child):last-child {
        justify-content: flex-end;
      }
      &.flex-row .nav-item.desktop-only {
        display: none;
      }
    }
    .nav-link {
      position: relative;
      :global(.media-item) {
        overflow: visible;
        object-fit: contain;
        /* prevent image from shrinking to zero width */
        width: fit-content;
        max-width: max-content;
      }
    }
    .nav-spacer {
      flex-grow: 1;
    }
  }

  :global(.navigation.flex-row .nav-item) {
    --button-outer-block-spacing: var(--default-button-outer-block-spacing);
    --button-outer-inline-spacing: calc(var(--default-button-outer-block-spacing) * 0.5);
  }
  :global(.navigation .nav-item .navigation-image) {
    max-height: var(--dynamic-island-max-nav-item-size);
  }
  /*
    The underlined surface drops the label's inline padding on purpose — the rule has to hug the
    word it belongs to — so the only thing left between two items is the 2px of outer spacing set
    above, and the underlines end up almost touching. The gap goes on the GROUP, not on the items:
    padding on the items would push the first and last one off the page's edge alignment.
  */
  :global(.navigation.flex-row .nav-group:has(.button-label.underlined)) {
    column-gap: var(--size-7);
  }
  /* :global(.navigation .nav-item.image-only) {
    --button-outer-block-spacing: 0;
  } */
  :global(.navigation .nav-item:has(.navigation-image) .button-label) {
    min-width: max-content;
  }
  :global(.navigation .nav-item .button-label .button-text) {
    min-width: fit-content;
  }
  :global(.navigation .nav-item:has(.navigation-image) .button-label .start-icon-container) {
    width: fit-content;
    padding-block: 0;
  }
  :global(.navigation .nav-item .navigation-image) {
    min-height: var(--nav-item-size, var(--icon-size));
    height: 100%;
    width: fit-content; /* for safari: needs to be explicitly set */
  }


  .navigation.flex-row {
    justify-content: space-between;
    .nav-separator {
      width: 1px;
      background-color: var(--color-default-stroke);
    }
    .nav-group {
      flex-grow: 1;
    }
  }

  .navigation.flex-column {
    row-gap: var(--size-10);
    /* Distribute items in navigation vertically, if there are more than two nav group */
    &:has(.nav-group:nth-child(n+3)) {
      justify-content: space-around;
    }
    :global(.nav-item .button-label .start-icon-container:first-child:last-child),
    :global(.nav-item .button-label .end-icon-container:first-child:last-child) {
      /* Buttons with no button-text when stacked vertically must also have a padding for icon only buttons. Usually they don't have a padding */
      padding-inline: var(--button-label-padding-inline);
    }
  }

  /* Groups */
  .nav-group.flex-row {
    .nav-link.image-only :global(.button-label) {
      /* padding-block: var(--size-3); */
      height: 100%;
      :global(.navigation-image) {
        --number-of-row-1-right-icons: 4; /* TODO: remove this once we have a proper way to count the number of admin icons */
        --admin-icon-size: var(--menu-item-min-size);
        max-width: calc(
          100vw - 
          var(--number-of-row-1-right-icons) * var(--admin-icon-size) 
          - 2 * var(--page-inline-spacing)
          - 3 * var(--button-label-padding-inline)
        );
      }
    }
  }
  .nav-group.flex-column {
    padding-inline-end: var(--size-2);
    /* Make all links full width */
    .nav-link {
      width: 100%;
    }
    /* Increase height of image-only links */
    .nav-link.image-only :global(.button-label) {      
      :global(.navigation-image) {
        height: calc(var(--heading1-font-size) * 2);
        max-width: calc(100vw - 2 * var(--page-inline-spacing) - var(--scrollbar-width));
      }
    }
    /* Render all items after first item smaller */
    .nav-separator ~ .nav-link :global(.button-label) {
      font-size: var(--preamble-font-size);
      line-height: var(--preamble-line-scale);
    }
  }
</style>